/**
 * DocumentActivityIndicator — 文档活动指示器
 *
 * 当外部来源（MCP 客户端、Gateway 等）正在操作当前文档时，在文档栏中显示紧凑的活动状态指示。
 * 通过 PaneContext + EditorStore 自动获取数据。
 *
 * 执行完成后 5 秒自动消失（由 editor-store handleDocumentActivity 负责清理）。
 */
import type { DocumentActivityEvent } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { Bot, Globe, PencilLine, Plug } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { usePaneChromeContext } from "./PaneContext";

/* ── helpers ── */

function sourceLabel(
    event: DocumentActivityEvent,
    t: ReturnType<typeof useT>,
): string {
    const s = event.source;
    switch (s.type) {
        case "mcp":
            return s.clientName ?? t("document.activity.mcpClient");
        case "gateway":
            return "Gateway";
        case "external-edit":
            return t("document.activity.externalEditor");
        case "unknown":
            return s.label ?? t("document.activity.unknownSource");
    }
}

function sourceIcon(event: DocumentActivityEvent, size = 12): ReactNode {
    switch (event.source.type) {
        case "mcp":
            return <Bot size={size} className="shrink-0" />;
        case "gateway":
            return <Globe size={size} className="shrink-0" />;
        case "external-edit":
            return <PencilLine size={size} className="shrink-0" />;
        default:
            return <Plug size={size} className="shrink-0" />;
    }
}

function operationLabel(
    op: DocumentActivityEvent["operation"],
    t: ReturnType<typeof useT>,
): string {
    switch (op) {
        case "read":
            return t("document.activity.read");
        case "write":
            return t("document.activity.write");
        case "execute":
            return t("document.activity.execute");
        case "completed":
            return t("document.activity.completed");
        case "failed":
            return t("document.activity.failed");
    }
}

function accentForOperation(
    op: DocumentActivityEvent["operation"],
): Parameters<typeof getThemeAccentStyle>[0] {
    switch (op) {
        case "failed":
            return "error";
        case "completed":
        case "write":
            return "success";
        case "execute":
        case "read":
            return "running";
        default:
            return "info";
    }
}

const EMPTY_DOCUMENT_ACTIVITIES: DocumentActivityEvent[] = [];

/* ── Compact inline indicator (self-contained, for document bars) ── */

export function usePaneDocumentActivities() {
    const { sessionId } = usePaneChromeContext();
    return useEditorStore((s) =>
        sessionId
            ? (s.documentActivities.get(sessionId) ?? EMPTY_DOCUMENT_ACTIVITIES)
            : EMPTY_DOCUMENT_ACTIVITIES,
    );
}

interface DocumentActivityIndicatorProps {
    activities?: DocumentActivityEvent[];
    className?: string;
}

/**
 * Self-contained activity indicator that reads from PaneContext + EditorStore.
 * Designed to sit inline within the document path bar.
 */
export function DocumentActivityIndicator({
    activities: providedActivities,
    className,
}: DocumentActivityIndicatorProps = {}) {
    if (providedActivities) {
        return (
            <DocumentActivityIndicatorContent
                activities={providedActivities}
                className={className}
            />
        );
    }

    return <PaneDocumentActivityIndicator className={className} />;
}

function PaneDocumentActivityIndicator({ className }: { className?: string }) {
    const activities = usePaneDocumentActivities();
    return (
        <DocumentActivityIndicatorContent
            activities={activities}
            className={className}
        />
    );
}

function DocumentActivityIndicatorContent({
    activities,
    className,
}: {
    activities: DocumentActivityEvent[];
    className?: string;
}) {
    const t = useT("editor");
    if (activities.length === 0) return null;

    const shown = activities.slice(0, 2);
    const hidden = activities.length - shown.length;

    return (
        <div className={cn("flex min-w-0 items-center gap-1.5 overflow-hidden", className)}>
            {shown.map((ev, i) => {
                const accent = accentForOperation(ev.operation);
                const isActive = ev.operation === "read" || ev.operation === "execute";
                return (
                    <span
                        key={`${ev.source.type}:${ev.source.type === "mcp" ? ev.source.sessionId : i}`}
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full theme-accent-text"
                        style={getThemeAccentStyle(accent)}
                    >
                        {isActive && (
                            <span className="relative flex h-1.5 w-1.5 shrink-0">
                                <span
                                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                                    style={{ backgroundColor: "currentColor" }}
                                />
                                <span
                                    className="relative inline-flex h-1.5 w-1.5 rounded-full"
                                    style={{ backgroundColor: "currentColor" }}
                                />
                            </span>
                        )}
                        {sourceIcon(ev, 10)}
                        <span className="font-medium truncate">{sourceLabel(ev, t)}</span>
                        <span className="text-muted-foreground whitespace-nowrap">
                            {operationLabel(ev.operation, t)}
                        </span>
                    </span>
                );
            })}
            {hidden > 0 && (
                <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap">
                    +{hidden}
                </span>
            )}
        </div>
    );
}
